import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
  type OAuthApi,
  type IdentityApi,
} from '@backstage/frontend-plugin-api';
import { ProductResponseSchema } from './types/product';
import type { ProductResponse } from './types/product';
import type {
  ProvisionJobRequest,
  ProvisionJobResponse,
} from './types/provisionJob';
import { ProvisionJobResponseSchema } from './types/provisionJob';
import type {
  ProvisionJobAppCIRequest,
  ProvisionJobAppCIResponse,
} from './types/provisionJobAppCI';
import { ProvisionJobAppCIResponseSchema } from './types/provisionJobAppCI';
import type {
  ProvisionJobRuntimeEnvRequest,
  ProvisionJobRuntimeEnvResponse,
} from './types/provisionJobRuntimeEnv';
import { ProvisionJobRuntimeEnvResponseSchema } from './types/provisionJobRuntimeEnv';
import { OrchWorkspacesResponseSchema } from './types/orchWorkspaces';
import type { OrchWorkspacesResponse } from './types/orchWorkspaces';
import { OrchRuntimeDetailsResponseSchema } from './types/orchRuntimeDetails';
import type { OrchRuntimeDetailsResponse } from './types/orchRuntimeDetails';
import {
  DeleteRuntimeJobResponseSchema,
  RuntimeDeleteEnvironmentSchema,
} from './types/orchRuntimeDelete';
import type {
  DeleteRuntimeJobResponse,
  RuntimeDeleteEnvironment,
} from './types/orchRuntimeDelete';
import { DeleteAppCIJobResponseSchema } from './types/appciDeleteJob';
import type { DeleteAppCIJobResponse } from './types/appciDeleteJob';
import {
  ValidationErrorResponseSchema,
  ErrorResponseSchema,
} from './types/errors';
import { ProductListResponseSchema } from './types/productList';
import type {
  ProductListResponse,
  ProductListScope,
} from './types/productList';
import { AddProductUserErrorResponseSchema } from './types/addProductUser';
import type { AddProductUserRequest } from './types/addProductUser';
import {
  createDummyProducts,
  createDummyOrchWorkspaces,
  createDummyOrchRuntimeDetails,
  createDummyDeleteAppCIJob,
  createDummyDeleteRuntimeJob,
} from './dummyData';

/**
 * BFF API のインターフェース定義
 *
 * BFF サービスへのリクエストをすべてこのインターフェースに集約する。
 * エンドポイントが増える場合はここにメソッドを追加する。
 *
 * ## メソッドの並び順ルール
 * 1. HTTP メソッド順: GET → POST → PUT → DELETE
 * 2. 同メソッド内はリソースパスのアルファベット順
 *    (例: /api/products → /api/products/:id → /api/products/:id/sub → /api/provision-jobs)
 */
export interface BffApi {
  /** プロダクト一覧を取得する */
  getProducts(scope?: ProductListScope): Promise<ProductListResponse>;
  /** プロダクト情報を取得する */
  getProduct(productId: string): Promise<ProductResponse>;
  /** プロダクトの Orchestrator ワークスペース情報を取得する */
  getOrchWorkspaces(productId: string): Promise<OrchWorkspacesResponse>;
  /** ランタイム環境詳細情報を取得する */
  getOrchRuntimeDetails(
    orchWorkspaceId: string,
    runtimeId: string,
  ): Promise<OrchRuntimeDetailsResponse>;
  /** AppCI 削除ジョブを開始する */
  deleteAppCIJob(
    productId: string,
    orchAppCIId: string,
  ): Promise<DeleteAppCIJobResponse>;
  /** ランタイム環境を削除する */
  deleteRuntimeJob(
    productId: string,
    orchWorkspaceId: string,
    orchRuntimeEnvId: string,
    env: RuntimeDeleteEnvironment,
  ): Promise<DeleteRuntimeJobResponse>;
  /** プロダクトへ利用者を追加する */
  addProductUser(productId: string, body: AddProductUserRequest): Promise<void>;
  /** clz.scale 払い出しジョブを作成する */
  createProvisionJob(body: ProvisionJobRequest): Promise<ProvisionJobResponse>;
  /** AppCI（GitHub リポジトリ）払い出しを開始する */
  provisionJobAppCI(
    body: ProvisionJobAppCIRequest,
  ): Promise<ProvisionJobAppCIResponse>;
  /** RuntimeEnv 払い出しを開始する */
  provisionJobRuntimeEnv(
    body: ProvisionJobRuntimeEnvRequest,
  ): Promise<ProvisionJobRuntimeEnvResponse>;
}

export const bffApiRef = createApiRef<BffApi>().with({
  id: 'plugin.idp-control-base.bff',
});

/**
 * Entra (Microsoft) のアクセストークン取得に失敗したことを表すエラー。
 * CEC テナント未所属のユーザーは BFF 向けのトークンを取得できずこのエラーになるため、
 * 呼び出し側でテナント参加申請画面への誘導などに分岐できるようエラー型で区別する。
 */
export class MicrosoftAuthRequiredError extends Error {
  constructor(cause: unknown) {
    super('Entra (CEC テナント) への認証に失敗しました。', { cause });
    this.name = 'MicrosoftAuthRequiredError';
  }
}

/**
 * BFF API のクライアント実装
 *
 * Backstage の proxy backend (`/api/proxy/bff/...`) を経由して
 * BFF サービスにリクエストを送る。
 * Microsoft (EntraID) のアクセストークンを Authorization ヘッダーで転送し、
 * BFF の OAuth2 Proxy 認証を通過させる。
 * 新しいエンドポイントを追加する場合は BffApi インターフェースにメソッドを定義し、
 * このクラスに実装を追加する。
 */
const GITHUB_SCOPES = 'repo user:email read:org workflow';

export class BffClient implements BffApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly microsoftAuthApi: OAuthApi;
  private readonly githubAuthApi: OAuthApi;
  private readonly identityApi: IdentityApi;
  private readonly bffScope: string;
  private readonly isDevelopment: boolean;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    microsoftAuthApi: OAuthApi;
    githubAuthApi: OAuthApi;
    identityApi: IdentityApi;
    /** BFF の OAuth2 Proxy が期待する Azure AD スコープ (例: api://<BFF_CLIENT_ID>/.default) */
    bffScope: string;
    /** true のとき Microsoft 認証をスキップする (開発環境用) */
    isDevelopment?: boolean;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.microsoftAuthApi = options.microsoftAuthApi;
    this.githubAuthApi = options.githubAuthApi;
    this.identityApi = options.identityApi;
    this.bffScope = options.bffScope;
    this.isDevelopment = options.isDevelopment ?? false;
  }

  /** proxy 経由の BFF ベース URL を解決する */
  private async getBaseUrl(): Promise<string> {
    try {
      const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');
      return `${proxyBaseUrl}/bff`;
    } catch (cause) {
      throw new Error(
        'Failed to resolve proxy base URL. Check that the proxy backend is configured correctly.',
        { cause },
      );
    }
  }

  /** レスポンスを JSON としてパースする */
  private async parseJson(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch (cause) {
      throw new Error('BFF API returned a non-JSON response.', { cause });
    }
  }

  /** レスポンスのステータスコードを検証する */
  private assertOk(response: Response, context: string): void {
    if (!response.ok) {
      throw new Error(
        `BFF API error (${context}): ${response.status} ${response.statusText}`,
      );
    }
  }

  /**
   * 認証ヘッダーを構築する。
   * 開発環境 (isDevelopment) では認証をスキップして空オブジェクトを返す。
   */
  private async buildAuthHeaders(): Promise<HeadersInit> {
    if (this.isDevelopment) {
      return {};
    }
    try {
      const token = await this.microsoftAuthApi.getAccessToken([this.bffScope]);
      return { Authorization: `Bearer ${token}` };
    } catch (cause) {
      // CEC テナント未所属などで Entra トークンを取得できない場合
      throw new MicrosoftAuthRequiredError(cause);
    }
  }

  /** リクエストごとに一意な X-Request-Id を生成する */
  private generateRequestId(): string {
    return (
      globalThis.crypto?.randomUUID?.() ??
      `req-${Date.now()}-${Math.random().toString(16).slice(2)}`
    );
  }

  /** detail が文字列のみの単純なエラーレスポンスからメッセージを抽出する */
  private async extractSimpleErrorMessage(response: Response): Promise<string> {
    const fallback = `${response.status} ${response.statusText}`;
    try {
      const json = await this.parseJson(response);
      const errorResult = ErrorResponseSchema.safeParse(json);
      return errorResult.success ? errorResult.data.detail : fallback;
    } catch {
      // JSON パース失敗時はステータステキストをそのまま使う
      return fallback;
    }
  }

  async getProducts(scope?: ProductListScope): Promise<ProductListResponse> {
    // 開発環境でダミーデータを返す
    if (this.isDevelopment) {
      return createDummyProducts();
    }

    const baseUrl = await this.getBaseUrl();
    const url = new URL(`${baseUrl}/api/products`);
    if (scope !== undefined) {
      url.searchParams.set('scope', scope);
    }

    const headers = await this.buildAuthHeaders();
    const response = await this.fetchApi.fetch(url.toString(), { headers });
    this.assertOk(response, 'getProducts');

    const json = await this.parseJson(response);

    try {
      return ProductListResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async getProduct(productId: string): Promise<ProductResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/products/${encodeURIComponent(productId)}`;

    // BFF の Azure AD アプリ向けのアクセストークンを取得し、Authorization ヘッダーで転送する
    // audience が BFF のアプリ ID になるよう、BFF のスコープ (bffScope) を指定する
    // BFF の OAuth2 Proxy がこのトークンを検証して認証を行う
    // 開発環境 (isDevelopment === true) では認証をスキップする
    const headers = await this.buildAuthHeaders();
    const response = await this.fetchApi.fetch(url, { headers });
    this.assertOk(response, 'getProduct');

    const json = await this.parseJson(response);

    try {
      return ProductResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async getOrchWorkspaces(productId: string): Promise<OrchWorkspacesResponse> {
    // 開発環境でダミーデータを返す
    if (this.isDevelopment) {
      return createDummyOrchWorkspaces(productId);
    }

    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/products/${encodeURIComponent(
      productId,
    )}/orch-workspaces`;

    const headers = await this.buildAuthHeaders();
    const response = await this.fetchApi.fetch(url, { headers });
    this.assertOk(response, 'getOrchWorkspaces');

    const json = await this.parseJson(response);

    try {
      return OrchWorkspacesResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async getOrchRuntimeDetails(
    orchWorkspaceId: string,
    runtimeId: string,
  ): Promise<OrchRuntimeDetailsResponse> {
    // 開発環境でダミーデータを返す
    if (this.isDevelopment) {
      return createDummyOrchRuntimeDetails();
    }

    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/workspace/${encodeURIComponent(
      orchWorkspaceId,
    )}/runtimeenvs/${encodeURIComponent(runtimeId)}/orch-runtime-details`;

    const headers = await this.buildAuthHeaders();
    const response = await this.fetchApi.fetch(url, { headers });
    this.assertOk(response, 'getOrchRuntimeDetails');

    const json = await this.parseJson(response);

    try {
      return OrchRuntimeDetailsResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async deleteAppCIJob(
    productId: string,
    orchAppCIId: string,
  ): Promise<DeleteAppCIJobResponse> {
    if (this.isDevelopment) {
      return createDummyDeleteAppCIJob();
    }

    const baseUrl = await this.getBaseUrl();
    const url = new URL(
      `${baseUrl}/api/delete-jobs/appci/${encodeURIComponent(orchAppCIId)}`,
    );
    url.searchParams.set('product_id', productId);

    const [authHeaders, githubToken, profileInfo] = await Promise.all([
      this.buildAuthHeaders(),
      this.githubAuthApi.getAccessToken(GITHUB_SCOPES),
      this.identityApi.getProfileInfo(),
    ]);

    if (!profileInfo.email) {
      throw new Error(
        '削除リクエストに必要なメールアドレスを取得できませんでした。再ログインしてからやり直してください。',
      );
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        'X-Request-Id': this.generateRequestId(),
        'X-GitHub-Token': githubToken,
        'X-Auth-Request-Email': profileInfo.email,
        ...authHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractSimpleErrorMessage(response));
    }

    const json = await this.parseJson(response);

    try {
      return DeleteAppCIJobResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async deleteRuntimeJob(
    productId: string,
    orchWorkspaceId: string,
    orchRuntimeEnvId: string,
    env: RuntimeDeleteEnvironment,
  ): Promise<DeleteRuntimeJobResponse> {
    if (this.isDevelopment) {
      return createDummyDeleteRuntimeJob();
    }

    const envValidation = RuntimeDeleteEnvironmentSchema.safeParse(env);
    if (!envValidation.success) {
      throw new Error(
        'Runtime 削除に必要な env 情報を取得できませんでした。Product Dashboard の一覧からこの画面を開き直してください。',
      );
    }

    const baseUrl = await this.getBaseUrl();
    const url = new URL(
      `${baseUrl}/api/delete-jobs/runtime/${encodeURIComponent(
        orchRuntimeEnvId,
      )}`,
    );
    url.searchParams.set('product_id', productId);
    url.searchParams.set('orch_workspace_id', orchWorkspaceId);
    url.searchParams.set('env', env);

    const [authHeaders, githubToken, profileInfo] = await Promise.all([
      this.buildAuthHeaders(),
      this.githubAuthApi.getAccessToken(GITHUB_SCOPES),
      this.identityApi.getProfileInfo(),
    ]);

    if (!profileInfo.email) {
      throw new Error(
        '削除リクエストに必要なメールアドレスを取得できませんでした。再ログインしてからやり直してください。',
      );
    }

    const response = await this.fetchApi.fetch(url.toString(), {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        'X-Request-Id': this.generateRequestId(),
        'X-GitHub-Token': githubToken,
        'X-Auth-Request-Email': profileInfo.email,
        ...authHeaders,
      },
    });

    if (!response.ok) {
      throw new Error(await this.extractSimpleErrorMessage(response));
    }

    const json = await this.parseJson(response);

    try {
      return DeleteRuntimeJobResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async addProductUser(
    productId: string,
    body: AddProductUserRequest,
  ): Promise<void> {
    if (this.isDevelopment) {
      return;
    }

    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/products/${encodeURIComponent(
      productId,
    )}/users`;

    const [authHeaders, profileInfo] = await Promise.all([
      this.buildAuthHeaders(),
      this.identityApi.getProfileInfo(),
    ]);

    if (!profileInfo.email) {
      throw new Error(
        'ユーザー追加に必要なメールアドレスを取得できませんでした。再ログインしてからやり直してください。',
      );
    }

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        'X-Request-Id': this.generateRequestId(),
        'X-Auth-Request-Email': profileInfo.email,
        ...authHeaders,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const json = await this.parseJson(response);
        const errorResult = AddProductUserErrorResponseSchema.safeParse(json);
        if (errorResult.success) {
          errorMessage = errorResult.data.detail.message;
        }
      } catch {
        // JSON パース失敗時はステータステキストをそのまま使う
      }
      throw new Error(errorMessage);
    }
  }

  async createProvisionJob(
    body: ProvisionJobRequest,
  ): Promise<ProvisionJobResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/provision-jobs/product`;

    const [authHeaders, githubToken] = await Promise.all([
      this.buildAuthHeaders(),
      this.githubAuthApi.getAccessToken(GITHUB_SCOPES),
    ]);
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        'X-GitHub-Token': githubToken,
        ...authHeaders,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const json = await this.parseJson(response);
        // 422 バリデーションエラー形式を優先してパースする
        const validationResult = ValidationErrorResponseSchema.safeParse(json);
        if (validationResult.success) {
          errorMessage = validationResult.data.detail
            .map(d => d.msg)
            .join('\n');
        } else {
          // 汎用エラー形式（detail が文字列）をパースする
          const errorResult = ErrorResponseSchema.safeParse(json);
          if (errorResult.success) {
            errorMessage = errorResult.data.detail;
          }
        }
      } catch {
        // JSON パース失敗時はステータステキストをそのまま使う
      }
      throw new Error(errorMessage);
    }

    const json = await this.parseJson(response);

    try {
      return ProvisionJobResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async provisionJobAppCI(
    body: ProvisionJobAppCIRequest,
  ): Promise<ProvisionJobAppCIResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/provision-jobs/appci`;

    const requestId = this.generateRequestId();

    const [authHeaders, githubToken, profileInfo] = await Promise.all([
      this.buildAuthHeaders(),
      this.githubAuthApi.getAccessToken(GITHUB_SCOPES),
      this.identityApi.getProfileInfo(),
    ]);

    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      'X-Request-Id': requestId,
      'X-GitHub-Token': githubToken,
      ...authHeaders,
    } as Record<string, string>;

    // X-Auth-Request-Email ヘッダーを追加（メールアドレスが存在する場合）
    if (profileInfo.email) {
      headers['X-Auth-Request-Email'] = profileInfo.email;
    }

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const json = await this.parseJson(response);
        // 422 バリデーションエラー形式を優先してパースする
        const validationResult = ValidationErrorResponseSchema.safeParse(json);
        if (validationResult.success) {
          errorMessage = validationResult.data.detail
            .map(d => d.msg)
            .join('\n');
        } else {
          // 汎用エラー形式（detail が文字列）をパースする
          const errorResult = ErrorResponseSchema.safeParse(json);
          if (errorResult.success) {
            errorMessage = errorResult.data.detail;
          }
        }
      } catch {
        // JSON パース失敗時はステータステキストをそのまま使う
      }
      throw new Error(errorMessage);
    }

    const json = await this.parseJson(response);

    try {
      return ProvisionJobAppCIResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }

  async provisionJobRuntimeEnv(
    body: ProvisionJobRuntimeEnvRequest,
  ): Promise<ProvisionJobRuntimeEnvResponse> {
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/api/provision-jobs/runtimeenv`;

    const [authHeaders, githubToken, profileInfo] = await Promise.all([
      this.buildAuthHeaders(),
      this.githubAuthApi.getAccessToken(GITHUB_SCOPES),
      this.identityApi.getProfileInfo(),
    ]);

    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      'X-GitHub-Token': githubToken,
      ...authHeaders,
    } as Record<string, string>;

    // X-Auth-Request-Email ヘッダーを追加（メールアドレスが存在する場合）
    if (profileInfo.email) {
      headers['X-Auth-Request-Email'] = profileInfo.email;
    }

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const json = await this.parseJson(response);
        // 422 バリデーションエラー形式を優先してパースする
        const validationResult = ValidationErrorResponseSchema.safeParse(json);
        if (validationResult.success) {
          errorMessage = validationResult.data.detail
            .map(d => d.msg)
            .join('\n');
        } else {
          // 汎用エラー形式（detail が文字列）をパースする
          const errorResult = ErrorResponseSchema.safeParse(json);
          if (errorResult.success) {
            errorMessage = errorResult.data.detail;
          }
        }
      } catch {
        // JSON パース失敗時はステータステキストをそのまま使う
      }
      throw new Error(errorMessage);
    }

    const json = await this.parseJson(response);

    try {
      return ProvisionJobRuntimeEnvResponseSchema.parse(json);
    } catch (cause) {
      throw new Error('BFF API returned an unexpected response schema.', {
        cause,
      });
    }
  }
}
